import { Button, Page, SearchInput, Table, Text } from '@dixa/lib-shared-habla';
import Fuse from 'fuse.js';
import { useCallback, useMemo, useState } from 'react';

import { appActions } from 'app/app.actions';
import { useCommands } from 'utils/commands';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

import { KeyboardKeybindingsBanner } from './components/banner/keyboardKeybindingsBanner';
import { KeyboardKeybindingsTableRow } from './components/row/keyboardKeybindingsTableRow';
import styles from './preferencesKeyboardShortcutsForm.module.scss';

const minMatchCharLength = 2;

export const PreferencesKeyboardShortcutsForm = (): JSX.Element => {
  const {
    enrichedCommands,
    resetAllUserKeybindings,
    unassignAllKeybindings,
    canResetAllRebindableKeybindings,
    canUnassignAllRebindableKeybindings,
  } = useCommands();

  const dispatch = useAppDispatch();
  const [searchTerm, setSearchTerm] = useState('');

  const filteredEnrichedCommands = useMemo(() => {
    const fuse = new Fuse(enrichedCommands, {
      keys: ['command.label', 'command.description', 'command.group'],
      threshold: 0.2,
      minMatchCharLength,
      distance: 200,
    });

    if (searchTerm.length >= minMatchCharLength) {
      const fuseResults = fuse.search(searchTerm).map(({ item }) => item);
      return fuseResults.length > 0 ? fuseResults : enrichedCommands;
    }

    return enrichedCommands;
  }, [enrichedCommands, searchTerm]);

  const showFeedbackModal = useCallback(() => {
    dispatch(
      appActions.showModal({
        modal: {
          type: 'app:feedbackForm',
          arg: {
            team: 'conversations',
            title: 'Keyboard shortcuts feedback',
          },
        },
      }),
    );
  }, [dispatch]);

  const table = (
    <Table stickyHeader hoverable>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Command</Table.Th>
          <Table.Th width="200px">Group</Table.Th>
          <Table.Th width="0" />
          <Table.Th width="35%">Keybindings</Table.Th>
          <Table.Th width="0" />
        </Table.Tr>
      </Table.Thead>

      <Table.Tbody>
        {filteredEnrichedCommands.map((enrichedCommand) => (
          <KeyboardKeybindingsTableRow
            key={enrichedCommand.command.name}
            enrichedCommand={enrichedCommand}
          />
        ))}
      </Table.Tbody>
    </Table>
  );

  return (
    <Page.Section
      title="All keyboard shortcuts"
      titleMeta={
        <span onClick={() => showFeedbackModal()}>
          <Text.Paragraph as="span" variant="4" className={styles.feedbackLink}>
            Send feedback
          </Text.Paragraph>
        </span>
      }
      controls={
        <>
          <SearchInput
            onSubmit={setSearchTerm}
            onToggle={(isOpen) => !isOpen && setSearchTerm('')}
          />

          <Button
            disabled={!canResetAllRebindableKeybindings}
            variant="secondary"
            onClick={() => resetAllUserKeybindings()}
          >
            Reset all
          </Button>

          <Button
            disabled={!canUnassignAllRebindableKeybindings}
            variant="secondary"
            onClick={() => unassignAllKeybindings()}
          >
            Unassign all
          </Button>
        </>
      }
      callout={<KeyboardKeybindingsBanner />}
    >
      {table}
    </Page.Section>
  );
};
